import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Clock,
  Users,
  Globe,
  Package,
  Star,
  Heart,
  Share2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Flag,
  BadgeCheck,
  X,
  Images,
  Calendar,
} from "lucide-react";
import axios from "axios";
import { useParams } from "react-router-dom";

// ─── DATA ───────────────────────────────────────────────────────────────

const REVIEWS = [
  {
    name: "Duc Trung",
    date: "May 26, 2025",
    rating: 5,
    comment: "Amazing tour! The guide was very knowledgeable.",
    avatar: "https://i.pravatar.cc/48?img=12",
  },
  {
    name: "Sarah M.",
    date: "Nov 28, 2025",
    rating: 4,
    comment: "Great experience overall, highly recommend!",
    avatar: "https://i.pravatar.cc/48?img=1",
  },
];

const PICKUP_POINTS = [
  "Nimman Area Hotels",
  "Old City Hotels",
  "Airport Plaza",
  "Meeting Point – McDonalds",
];

// ─── LIGHTBOX ───────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-2"
      >
        <X className="w-6 h-6" />
      </button>

      <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
        {idx + 1} / {images.length}
      </span>

      <button
        onClick={(e) => {
          e.stopPropagation();
          setIdx((i) => (i - 1 + images.length) % images.length);
        }}
        className="absolute left-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <img
        src={images[idx]}
        alt={images[idx]}
        className="max-w-[88vw] max-h-[82vh] object-contain rounded-xl"
        onClick={(e) => e.stopPropagation()}
      />

      <button
        onClick={(e) => {
          e.stopPropagation();
          setIdx((i) => (i + 1) % images.length);
        }}
        className="absolute right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div
        className="absolute bottom-4 flex gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, i) => (
          <button
            key={img.id}
            onClick={() => setIdx(i)}
            className={`w-14 h-10 rounded-lg overflow-hidden border-2 transition-all ${
              i === idx ? "border-white" : "border-transparent opacity-50"
            }`}
          >
            <img src={img} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── STARS ──────────────────────────────────────────────────────────────
function Stars({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i <= rating ? "fill-amber-400 text-amber-400" : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );
}

// ─── LEFT: TOUR DETAIL ──────────────────────────────────────────────────
// Replace the entire TourDetail component with this version

function TourDetail({ tour }) {
  const [readMore, setReadMore] = useState(false);
  const [openDay, setOpenDay] = useState(null); // Track which day is open (by index)

  const toggleDay = (index) => {
    setOpenDay(openDay === index ? null : index);
  };



  return (
    <div className="space-y-8">
      {/* OVERVIEW STATS */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4 hover:border-[#CC3316] hover:bg-[#CC3316]/5 transition-all">
            <span className="text-[#CC3316]">
              <Clock className="w-5 h-5" />
            </span>
            <div>
              <p className="text-xs sm:text-sm text-gray-400 font-medium uppercase tracking-wide">
                Duration
              </p>
              <p className="text-xs sm:text-sm font-bold text-gray-900">
                {tour?.overview?.duration}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4 hover:border-[#CC3316] hover:bg-[#CC3316]/5 transition-all">
            <span className="text-[#CC3316]">
              <Users className="w-5 h-5" />
            </span>
            <div>
              <p className="text-xs sm:text-sm text-gray-400 font-medium uppercase tracking-wide">
                Travelers
              </p>
              <p className="text-xs sm:text-sm font-bold text-gray-900">
                {tour?.overview?.travelers} Guests
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4 hover:border-[#CC3316] hover:bg-[#CC3316]/5 transition-all">
            <span className="text-[#CC3316]">
              <Package className="w-5 h-5" />
            </span>
            <div>
              <p className=" text-xs sm:text-sm text-gray-400 font-medium uppercase tracking-wide">
                Tour Type
              </p>
              <p className="text-xs sm:text-sm font-bold text-gray-900">
                {tour?.tourType?.name}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4 hover:border-[#CC3316] hover:bg-[#CC3316]/5 transition-all">
            <span className="text-[#CC3316]">
              <Globe className="w-5 h-5" />
            </span>
            <div>
              <p className="text-xs sm:text-sm text-gray-400 font-medium uppercase tracking-wide">
                Language
              </p>
              <p className="text-xs sm:text-sm font-bold text-gray-900">
                {tour?.overview?.languages?.join(", ")}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <p
            className={`text-lg text-gray-600 leading-relaxed whitespace-pre-line ${
              !readMore ? "line-clamp-3" : ""
            }`}
          >
            {tour?.description}
          </p>
          <button
            onClick={() => setReadMore(!readMore)}
            className="mt-2 text-sm font-semibold text-[#CC3316] hover:text-[#99220f] flex items-center gap-1"
          >
            {readMore ? "Show less" : "Read more"}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                readMore ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* HIGHLIGHTS */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Highlights</h2>
        <div className="space-y-2">
          {tour?.highlights?.map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-3 bg-[#CC3316]/5 border border-[#CC3316]/20 rounded-xl px-4 py-3"
            >
              <BadgeCheck className="w-5 h-5 text-[#CC3316] mt-0.5 flex-shrink-0" />
              <span className="text-lg text-gray-800">{item}</span>
            </div>
          ))}
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* INCLUDES / EXCLUDES */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Includes / Excludes
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <p className="text-sm font-bold text-yellow-700 uppercase tracking-widest mb-3">
              Included
            </p>
            <ul className="space-y-2">
              {tour?.includes?.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-lg text-gray-700"
                >
                  <CheckCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-5">
            <p className="text-xs font-bold text-red-600 uppercase tracking-widest mb-3">
              Not Included
            </p>
            <ul className="space-y-2">
              {tour?.excludes?.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-lg text-gray-700"
                >
                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* CANCELLATION */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Cancellation Policy
        </h2>
        <div className="flex items-start gap-4 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
          <div className="w-9 h-9 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0 text-yellow-600 font-bold text-sm">
            i
          </div>
          <div>
            <p className="font-semibold text-yellow-900 text-sm">
              Free Cancellation Available
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Cancel up to 24 hours in advance for a full refund.
            </p>
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* REDESIGNED ITINERARY SECTION (Advanced & Smooth) */}
      <section>
        <h2 className="text-xl md:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">
          Itinerary
        </h2>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <div className="space-y-3">
            {tour?.itinerary?.map((day, index) => {
              const isOpen = openDay === index;
              return (
                <div
                  key={day.day}
                  className={`border rounded-xl overflow-hidden transition-all duration-200 ${
                    isOpen
                      ? "border-[#CC3316] shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <button
                    onClick={() => toggleDay(index)}
                    className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex gap-3 sm:gap-4 flex-1 text-left items-center">
                      <span
                        className={`font-semibold px-3 py-1 rounded-full text-xs sm:text-sm whitespace-nowrap ${
                          isOpen
                            ? "bg-[#CC3316] text-white"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {day?.day}
                      </span>
                      <span className="flex-1 font-medium text-sm sm:text-base text-gray-800">
                        {new Date(day?.date).toLocaleDateString("en-GB").replace(/\//g, "-")}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 ml-2 flex-shrink-0 text-gray-500 transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Content with smooth height transition */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="p-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
                      {day?.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* REVIEWS */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Customer Reviews
        </h2>
        <div className="flex items-center gap-6 bg-gray-900 text-white rounded-xl p-5 mb-5">
          <div className="text-center">
            <p className="text-5xl font-black">4.8</p>
            <Stars rating={5} />
            <p className="text-xs text-gray-400 mt-1">2 reviews</p>
          </div>
          <div className="flex-1 space-y-2">
            {[
              ["Guide", 5],
              ["Service", 5],
              ["Transport", 5],
              ["Organization", 4],
            ].map(([label, score]) => (
              <div key={label} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-20">{label}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full">
                  <div
                    className="h-full bg-[#CC3316] rounded-full"
                    style={{ width: `${(score / 5) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-300 w-8">{score}/5</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          {REVIEWS.map((r, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <img
                    src={r.avatar}
                    alt={r.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{r.name}</p>
                    <p className="text-xs text-gray-400">{r.date}</p>
                  </div>
                </div>
                <Stars rating={r.rating} />
              </div>
              <p className="text-sm text-gray-700">{r.comment}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── RIGHT: BOOKING FORM ─────────────────────────────────────────────────
function BookingForm({ tour }) {

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    date: "",
    adult: 1,
    children: 0,
    infant: 0,
  });
  const [loading, setLoading] = useState(false);

  
  const handleBooking = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/tour-booking/create`,
        {
          ...form,
          tour: tour?._id,
          totalPrice: total,
        }
      );

      console.log(response.data);
      toast.success("Tour Booking Successful");
      setForm({
        fullName: "",
        email: "",
        phone: "",
        date: "",
        adult: 1,
        children: 0,
        infant: 0,
      });

    } catch (error) {
      console.log(error);
      toast.error("Tour Booking Failed")
    } finally {
      setLoading(false);
    }
  };


  const total =
    form.adult * (tour?.offerPrice || 0) +
    form.children * (tour?.childrenPrice || 0);

  const update = (field, val) => setForm((p) => ({ ...p, [field]: val }));

  function Counter({ label, sub, field, min = 0 }) {
    return (
      <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">{label}</p>
          <p className="text-xs text-gray-400">{sub}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => update(field, Math.max(min, form[field] - 1))}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#CC3316]/10 hover:border-[#CC3316] font-bold text-lg leading-none"
          >
            −
          </button>
          <span className="w-5 text-center font-bold">{form[field]}</span>
          <button
            type="button"
            onClick={() => update(field, form[field] + 1)}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#CC3316]/10 hover:border-[#CC3316] font-bold text-lg leading-none"
          >
            +
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky h-[70%] z-50 top-6">
      {/* Header */}
      <div className="mb-5 ">
        <h2 className="text-2xl font-bold text-gray-900">Book Your Tour</h2>
        <p className="text-sm text-gray-500 mt-1">
          From{" "}
          <span className="text-[#CC3316] font-bold text-lg">
            ₹{tour?.offerPrice}
          </span>{" "}
          / person
        </p>
      </div>

      <div className="space-y-3">
        <input
          type="text"
          placeholder="Full Name"
          value={form.fullName}
          onChange={(e) => update("fullName", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#CC3316]"
        />

        <input
          type="email"
          placeholder="Email Address"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#CC3316]"
        />

        <input
  type="text"
  placeholder="Phone Number"
  value={form.phone}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, ""); // only digits
    if (value.length <= 10) {
      update("phone", value);
    }
  }}
  maxLength={10}
  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#CC3316]"
/>

        

        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={form.date}
            onChange={(e) => update("date", e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#CC3316]"
          />
        </div>

        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Travellers
          </p>
          <Counter
            label="Adults"
            sub={`12+ years · ₹${tour?.offerPrice}`}
            field="adult"
            min={1}
          />
          <Counter
            label="Children"
            sub={`4–12 years · ₹${tour?.childrenPrice}`}
            field="children"
          />
          <Counter label="Infants" sub="Below 4 years · Free" field="infant" />
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="mt-4 pt-4 border-t border-gray-100 space-y-1.5">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Adults × {tour?.offerPrice}</span>
          <span className="font-semibold">
            ₹{(form.adult * tour?.offerPrice).toLocaleString()}
          </span>
        </div>
        {form.children > 0 && (
          <div className="flex justify-between text-sm text-gray-600">
            <span>Children × {form.children}</span>
            <span className="font-semibold">
              ₹{(form.children * tour?.childrenPrice).toLocaleString()}
            </span>
          </div>
        )}
        {form.infant > 0 && (
          <div className="flex justify-between text-sm text-gray-600">
            <span>Infants × {form.infant}</span>
            <span className="font-semibold text-[#CC3316]">Free</span>
          </div>
        )}
      </div>

      {/* Total */}
      <div className="mt-4 bg-[#CC3316] text-white rounded-xl px-5 py-4 flex items-center justify-between">
        <span className="font-semibold">Total Price</span>
        <span className="text-2xl font-black">₹{total.toLocaleString()}</span>
      </div>

      {/* Submit Button */}
      <button
  onClick={handleBooking}
  disabled={loading}
  className={`w-full mt-3 py-3.5 rounded-xl font-bold text-white transition-all ${
    loading
      ? "bg-[#CC3316]/50 cursor-not-allowed"
      : "bg-[#CC3316] hover:bg-[#99220f] hover:shadow-md"
  }`}
>
  {loading ? "Processing..." : "Book Now"}
</button>

      <p className="text-center text-xs text-gray-400 mt-3">
        🔒 Secure payment · Free cancellation
      </p>
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────
export default function ServiceDetails() {
  const [mainImg, setMainImg] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [liked, setLiked] = useState(false);

  //const smallImages = IMAGES.filter((_, i) => i !== mainImg);

  const { id } = useParams();
  const [tour, setTour] = useState(null);

  const fetchToursById = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/tour-details/get-by-id/${id}`,
      );
      setTour(response?.data);
      console.log(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchToursById();
  }, []);

   useEffect(() => {
      window.scrollTo(0, 0);
    }, []);


  return (
    <div className="min-h-screen bg-gray-50">
      {/* ═══ HERO BANNER ═══ */}
      <div className="relative h-[420px] w-full overflow-hidden">
        <img
          src={tour?.images?.[0]}
          alt="Tour Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/50" />

        {/* Banner Content */}
      </div>

      {/* ══ BANNER SECTION (top) ══ */}
      <div className="bg-white border-b border-gray-100 px-6 lg:px-20 py-10">
        {/* Breadcrumb */}
        <p className="text-sm text-gray-500 mb-3">
          <span className="hover:underline cursor-pointer">Home</span>
          <span className="mx-2 text-gray-300">/</span>
          <span className="text-gray-800 font-medium">{tour?.name}</span>
        </p>

        {/* Title + Like/Share */}
        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-2xl lg:text-3xl font-extrabold text-gray-900 leading-tight">
            {tour?.name}
          </h1>
          <div className="flex gap-2 flex-shrink-0 mt-1">
            <button className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
              <Share2 className="w-5 h-5 text-gray-500" />
            </button>
            <button
              onClick={() => setLiked(!liked)}
              className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <Heart
                className={`w-5 h-5 ${
                  liked ? "fill-red-500 text-red-500" : "text-gray-500"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Rating + Location */}
        <div className="flex items-center gap-3 text-sm mb-6">
          <Star className="w-4 h-4 fill-orange-400 text-orange-400" />
          <span className="font-bold text-gray-900">{tour?.review}</span>
          <span className="text-gray-400">(2 reviews)</span>
          <span className="text-gray-300">·</span>
          <span className="text-gray-600 font-medium">{tour?.location}</span>
        </div>

        {/* ── IMAGE GRID ── */}
        {/* ── IMAGE GRID ── */}
<div className="flex gap-3 w-full">
  {/* LEFT BIG IMAGE - 60% */}
  <div
    className="relative h-[200px] w-[50%] sm:h-[420px] sm:w-[60%] rounded-2xl overflow-hidden cursor-pointer group"
    onClick={() => setLightbox(0)}
  >
    <img
      src={tour?.images?.[0]}
      alt={tour?.name}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
    />
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
  </div>

  {/* RIGHT SIDE 2x2 GRID - 40% */}
  <div className="grid grid-cols-2 grid-rows-2 gap-3 w-[50%] h-[200px] sm:h-[420px] sm:w-[40%]">
    {tour?.images?.slice(1, 5).map((img, i) => (
      <div
        key={i}
        className="relative rounded-2xl overflow-hidden cursor-pointer group"
        onClick={() => setLightbox(i + 1)}
      >
        <img
          src={img}
          alt={tour?.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
      </div>
    ))}
  </div>
</div>
</div>
{/* ══ TWO COLUMN LAYOUT (Scrollable Left + 50% Fixed Right) ══ */}
<div className="w-full px-4 lg:px-20 py-8">
  <div className="flex flex-col lg:flex-row gap-8">

    {/* LEFT — Scrollable */}
    <div className="w-full lg:w-[60%]  overflow-y-auto pr-2">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 lg:p-8">
        <TourDetail tour={tour} />
      </div>
    </div>

    {/* RIGHT — Sticky 50% */}
    <div className="w-full lg:w-[40%]">
      <div className="sticky top-16">
        <BookingForm tour={tour} />
      </div>
    </div>

  </div>
</div>

      {/* Lightbox */}
      {lightbox !== null && (
        <Lightbox
          images={tour?.images}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
          tour={tour}
        />
      )}
    </div>
  );
}
